import { Checkbox } from '@fluentui/react';
import { IVerticalStackedChartProps, IVSChartDataPoint, VerticalStackedBarChart } from '@fluentui/react-charting';
import AppContext from 'App/AppContext';
import { IPLabel, IPSpinner, IPText } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import PeriodFilter, { IPeriodFilterData } from 'components/Filter/PeriodFilter';
import {
  getPeriodFilterEndDate,
  getPeriodFilterStartDate,
  getPeriodURLParams,
} from 'components/Filter/PeriodFilterHelper';
import KeyValueTagPicker from 'components/Pickers/KeyValueTagPicker';
import TenantPicker, { PickerTenant, TenantPickerMode } from 'components/Pickers/TenantPicker';
import { KeyValueTags } from 'components/Tags/KeyValueTag';
import { globalStackTokensGapMedium } from 'globalStyles';
import { Widget73ResultDTO } from 'models/dto/widgets/widget73DTO';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import Tag from 'models/tag';
import { isPartnerWidgetType, WidgetSizes, WidgetTenantConfig, WidgetWidth } from 'models/widget';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetWidgetTenantTaskStatus } from 'services/Api/dashboardService';
import { apiGetTagsForTenants } from 'services/Api/tagService';
import { apiRequest } from 'services/Auth/authConfig';
import { fromApiDateOptional } from 'utils/datetime';
import { getTaskStateColor, getTaskStateForColor } from 'utils/task';
import { IWidgetConfigRendererProps } from '../WidgetConfigRenderer';
import { IWidgetRendererProps } from '../WidgetRenderer';
import {
  getConfiguredTenantIds,
  getTenantWidgetTenantById,
  getTenantWidgetTenantId,
  getTenantWidgetTenantName,
  sortTenantWidgetTenantsByName,
  TenantWidgetTenant,
  WithTenantId,
  withTenantId,
} from './tenantWidgetData';
import { useTenantDrillDown } from './useTenantDrillDown';

//A tag id only means something inside one tenant. This widget spans tenants - for a partner, tenants it holds no
//tags in at all - so the selected tags are persisted by name and value, which is what identifies the same tag
//everywhere. tagIds is kept for configs written before that and for the picker, but it is not what the query uses.
export const tagToTagKey = (tag: Tag): string => `${tag.tagName}^${tag.tagValue}`;

export const tagKeysToTags = (tagKeys: string[]): Tag[] => {
  return tagKeys.map((tagKey) => {
    const separatorIdx = tagKey.indexOf('^');
    const newTag = new Tag();
    newTag.tagName = separatorIdx >= 0 ? tagKey.substring(0, separatorIdx) : tagKey;
    newTag.tagValue = separatorIdx >= 0 ? tagKey.substring(separatorIdx + 1) : '';

    return newTag;
  });
};

export class WidgetTenantTaskStatusConfig extends WidgetTenantConfig {
  tagIds: number[];

  tagKeys: string[];

  showTags: boolean;

  tags: Tag[];

  useAndForTagGroups: boolean;

  overDeadline: boolean;

  period?: IPeriodFilterData;

  constructor() {
    super();
    this.tagIds = [];
    this.tagKeys = [];
    this.showTags = false;
    this.tags = [];
    this.overDeadline = false;
    this.useAndForTagGroups = false;
  }

  load(raw: string | undefined) {
    if (raw) {
      try {
        const newRawConfig = JSON.parse(raw);
        this.loadTenant(newRawConfig);
        this.tagIds = newRawConfig.tagIds ?? [];
        this.tagKeys = newRawConfig.tagKeys ?? [];
        this.showTags = newRawConfig.showTags ?? false;
        this.overDeadline = newRawConfig.overDeadline ?? false;
        this.period = newRawConfig.period;
        this.useAndForTagGroups = newRawConfig.useAndForTagGroups ?? false;
      } catch {
        //ignore
      }
    }
  }

  //The selected tags, taken from the persisted names where they exist. The cache holds only the signed-in tenant's
  //tags, so for a partner widget it can neither confirm nor deny a customer's tag: falling back to it would drop
  //every selected tag and silently run the query with no tag filter at all.
  resolveTags(cache: GlobalDataCache): Tag[] {
    if (this.tagKeys.length > 0) {
      return tagKeysToTags(this.tagKeys);
    }

    return cache.tags.getItemsForId(this.tagIds).filter((t) => t.tagId !== 0);
  }

  clone(): WidgetTenantTaskStatusConfig {
    const newConfig = this.cloneTenant() as WidgetTenantTaskStatusConfig;
    newConfig.tagIds = [...this.tagIds];
    newConfig.tagKeys = [...this.tagKeys];
    newConfig.tags = [...this.tags];
    newConfig.showTags = this.showTags;
    newConfig.overDeadline = this.overDeadline;
    newConfig.period = this.period;
    newConfig.useAndForTagGroups = this.useAndForTagGroups;

    return newConfig;
  }
}

interface IWidgetTenantTaskStatusProps extends IWidgetRendererProps {}

const WidgetTenantTaskStatus = (props: IWidgetTenantTaskStatusProps) => {
  const { t } = useTranslation(['translation', 'widgets']);
  const appContext = useContext(AppContext);
  const [data, setData] = useState<Widget73ResultDTO[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [tenants, setTenants] = useState<TenantWidgetTenant[]>([]);
  const [tenantsToShow, setTenantsToShow] = useState<TenantWidgetTenant[]>([]);
  const [config, setConfig] = useState<WidgetTenantTaskStatusConfig | undefined>(undefined);

  useEffect(() => {
    const loadData = async () => {
      try {
        if (isLoading) return;

        setIsLoading(true);

        await appContext.globalDataCache.tags.getItems();

        const config = loadConfig();
        setConfig(config);

        const tenants: TenantWidgetTenant[] = isPartnerWidgetType(props.widget.widgetType)
          ? props.sharedData?.partnerCustomers ?? []
          : props.sharedData?.userOrgUnits ?? [];
        setTenants(tenants);

        //calculate start and end dates
        const customStart = fromApiDateOptional(config.period?.customStart);
        const customEnd = fromApiDateOptional(config.period?.customEnd);
        const start = getPeriodFilterStartDate(
          config.period?.option,
          config.period?.auditYear,
          customStart,
          appContext,
        );
        const end = getPeriodFilterEndDate(config.period?.option, config.period?.auditYear, customEnd, appContext);
        const tenantIds = getConfiguredTenantIds(config.tenants, tenants);
        if (tenantIds.length === 0) {
          setData([]);
          setTenantsToShow([]);

          return;
        }

        const accessToken = await appContext.getAccessToken(apiRequest.scopes);

        const data = await apiGetWidgetTenantTaskStatus(
          tenantIds,
          config.tags,
          config.useAndForTagGroups,
          config.overDeadline,
          start,
          end,
          accessToken,
          props.abortSignal,
        );

        setData(data);

        const tenantsToShow = tenants.filter((tenant) =>
          data.some((s) => s.tenantId === getTenantWidgetTenantId(tenant)),
        );
        sortTenantWidgetTenantsByName(tenantsToShow);
        setTenantsToShow(tenantsToShow);
      } catch (err) {
        appContext.setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget, props.sharedData?.partnerCustomers, props.sharedData?.userOrgUnits]);

  const loadConfig = (): WidgetTenantTaskStatusConfig => {
    const newConfig = new WidgetTenantTaskStatusConfig();
    newConfig.load(props.widget.widgetConfig);
    newConfig.tags = newConfig.resolveTags(appContext.globalDataCache);

    return newConfig;
  };

  const getData = (): IVerticalStackedChartProps[] => {
    if (data.length === 0 || tenantsToShow.length === 0) return [];

    const graphData: WithTenantId<IVerticalStackedChartProps>[] = tenantsToShow.map((tenant) => {
      const tenantId = getTenantWidgetTenantId(tenant);
      const tenantData = data.find((s) => s.tenantId === tenantId);
      const tenantName = getTenantWidgetTenantName(tenant);
      tenantData?.data.sort((a, b) => a.sortOrder - b.sortOrder);

      const dataPoints: IVSChartDataPoint[] =
        tenantData?.data.map((s, idx) => {
          //The server now returns each tenant's own state name, because task states are per tenant copies that
          //can be renamed independently. The cache only holds the signed-in tenant's names, so it is a fallback.
          const stateName = s.trans?.[0]?.state || s.state || appContext.globalDataCache.taskStates.get(s.taskStateId).state;

          return {
            legend: stateName,
            data: s.taskCount ?? 0,
            color: getTaskStateColor(s.taskStateId),
            xAxisCalloutData: tenantName,
          };
        }) ?? [];

      return {
        chartData: withTenantId(dataPoints, tenantId),
        xAxisPoint: tenantName,
        tenantId,
      };
    });

    return graphData;
  };

  const drillDown = useTenantDrillDown();

  const onClick = (data: IVSChartDataPoint) => {
    const tenant = getTenantWidgetTenantById(tenants, (data as WithTenantId<IVSChartDataPoint>).tenantId);
    if (!tenant || !config || !data.color) return;
    const filter = getTaskStateForColor(data.color);

    //for tags, use name^value format because tags have different id's in different tenants
    let tagStr = '';
    if (config.tags.length > 0) {
      tagStr = `&tags=${config.tags.map((t) => `${t.tagName}^${t.tagValue}`).join(',')}`;
      tagStr += `&useAndForTagGroups=${config.useAndForTagGroups}`;
    }

    let periodStr = '';
    if (config.period) {
      periodStr += `&${getPeriodURLParams(config.period)}`;
    }

    const deadlineStr = config.overDeadline ? '&deadline=deadlineTooLate' : '';

    const url = `/tasks/alltasks?status=${filter}${deadlineStr}${tagStr}${periodStr}`;

    drillDown(tenant, url, isPartnerWidgetType(props.widget.widgetType));
  };

  //
  // Main render
  //
  if (!data || isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  if (!isLoading && (data.length === 0 || tenantsToShow.length === 0)) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPText>{t('translation:General.Notifications.NoItemsFilter')}</IPText>
      </IPStack>
    );
  }

  const shouldRotateXAxisLabels = tenantsToShow.length > 10 && props.widget.widgetSize !== WidgetSizes.Large;

  return (
    <IPStack verticalFill>
      {config && config.tags.length > 0 && config.showTags && (
        <IPStack.Item styles={{ root: { maxHeight: 44, overflowY: 'hidden' } }}>
          <KeyValueTags tags={config.tags} compact tagMaxWidth={200} />
        </IPStack.Item>
      )}
      <IPStack.Item grow>
        <VerticalStackedBarChart
          data={getData()}
          height={250}
          width={WidgetWidth[props.widget.widgetSize] - 80}
          wrapXAxisLables={!shouldRotateXAxisLabels}
          rotateXAxisLables={shouldRotateXAxisLabels}
          barWidth={'auto'}
          legendProps={{
            centerLegends: true,
          }}
          onBarClick={(ev, data) => onClick(data as IVSChartDataPoint)}
        />
      </IPStack.Item>
    </IPStack>
  );
};

export default WidgetTenantTaskStatus;

//
// Config
//
interface IWidgetConfigWidgetTenantTaskStatusProps extends IWidgetConfigRendererProps {}

export const WidgetConfigTenantTaskStatus = (props: IWidgetConfigWidgetTenantTaskStatusProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'widgets', 'tasks']);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedTenants, setSelectedTenants] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [showTags, setShowTags] = useState<boolean>(false);
  const [useAndForTagGroups, setUseAndForTagGroups] = useState<boolean>(false);
  const [overDeadline, setOverDeadline] = useState<boolean>(false);
  const [period, setPeriod] = useState<IPeriodFilterData | undefined>(undefined);
  const [allowedTags, setAllowedTags] = useState<Tag[]>([]);
  const [allTenants, setAllTenants] = useState<PickerTenant[]>([]);
  const [currentWidgetId, setCurrentWidgetId] = useState<number>(-1);

  useEffect(() => {
    if (currentWidgetId === -1 || currentWidgetId !== props.widget.widgetId) {
      setCurrentWidgetId(props.widget.widgetId);
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  useEffect(() => {
    loadTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenants, allTenants]);

  const loadConfig = (): WidgetTenantTaskStatusConfig => {
    const newConfig = new WidgetTenantTaskStatusConfig();
    newConfig.load(props.widget.widgetConfig);
    newConfig.tags = newConfig.resolveTags(appContext.globalDataCache);

    return newConfig;
  };

  const loadData = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      await appContext.globalDataCache.tags.getItems();
      const config = loadConfig();
      setSelectedTenants(config.tenants);

      if (config.tags) {
        setSelectedTags(config.tags);
      }

      setShowTags(config.showTags);
      setPeriod(config.period);
      setOverDeadline(config.overDeadline);
      setUseAndForTagGroups(config.useAndForTagGroups);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTags = async () => {
    const tenantIds = selectedTenants.length > 0 ? selectedTenants : allTenants.map((tenant) => tenant.tenantId);
    if (tenantIds.length > 0) {
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const tags = await apiGetTagsForTenants(tenantIds, accessToken);
      setAllowedTags(tags);
    } else {
      setAllowedTags([]);
    }
  };

  const getConfig = (
    tenants: string[],
    tags: Tag[],
    showTags: boolean,
    overDeadline: boolean,
    period: IPeriodFilterData | undefined,
    useAndForTagGroups: boolean,
  ): WidgetTenantTaskStatusConfig => {
    const config = new WidgetTenantTaskStatusConfig();
    config.tenants = tenants;
    config.tagIds = tags.map((t) => t.tagId);
    config.tagKeys = tags.map(tagToTagKey);
    config.showTags = showTags;
    config.overDeadline = overDeadline;
    config.period = period;
    config.useAndForTagGroups = useAndForTagGroups;

    return config;
  };

  const updateTenants = (items: PickerTenant[]) => {
    const tenants = items.map((i) => i.tenantId);
    setSelectedTenants(tenants);
    const config = getConfig(tenants, selectedTags, showTags, overDeadline, period, useAndForTagGroups);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const onAdd = (tag: Tag) => {
    const newTags = [...selectedTags, tag];
    setSelectedTags(newTags);
    const config = getConfig(selectedTenants, newTags, showTags, overDeadline, period, useAndForTagGroups);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const onRemove = (tag: Tag) => {
    const newTags = selectedTags.filter((t) => t.tagId !== tag.tagId);
    setSelectedTags(newTags);
    const config = getConfig(selectedTenants, newTags, showTags, overDeadline, period, useAndForTagGroups);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const onSetShowTags = (checked: boolean) => {
    setShowTags(checked);
    const config = getConfig(selectedTenants, selectedTags, checked, overDeadline, period, useAndForTagGroups);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const onSetOverDeadline = (checked: boolean) => {
    setOverDeadline(checked);
    const config = getConfig(selectedTenants, selectedTags, showTags, checked, period, useAndForTagGroups);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const onSetPeriod = (period: IPeriodFilterData | undefined) => {
    setPeriod(period);
    const config = getConfig(selectedTenants, selectedTags, showTags, overDeadline, period, useAndForTagGroups);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const onSetUseAndForTagGroups = (checked: boolean) => {
    setUseAndForTagGroups(checked);
    const config = getConfig(selectedTenants, selectedTags, showTags, overDeadline, period, checked);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  //
  // Main render
  //
  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill tokens={globalStackTokensGapMedium}>
      <IPStack.Item>
        <TenantPicker
          mode={isPartnerWidgetType(props.widget.widgetType) ? TenantPickerMode.Partner : TenantPickerMode.User}
          onSelect={updateTenants}
          selectedTenants={selectedTenants}
          setAllTenants={setAllTenants}
          emptySelectionMeansAll
        />
      </IPStack.Item>
      <IPStack.Item>
        <PeriodFilter
          showOption={true}
          showCurrentAuditYear={true}
          storageKey={undefined}
          onUpdatePeriodOption={onSetPeriod}
          period={period}
          showAll={true}
        />
      </IPStack.Item>
      <IPStack.Item>
        <Checkbox
          checked={overDeadline}
          label={t('widgets:TenantTaskStatusOrgUnit.Config.LabelOverDeadline')}
          onChange={(ev, checked) => {
            onSetOverDeadline(checked ?? false);
          }}
        />
      </IPStack.Item>
      <IPStack.Item>
        <IPLabel>{t('widgets:TenantTaskStatusOrgUnit.Config.Label')}</IPLabel>
        <KeyValueTagPicker
          itemLimit={5}
          selectedTags={selectedTags}
          onAdd={onAdd}
          onRemove={onRemove}
          isLoading={isLoading}
          allowCreate={false}
          allowedTags={allowedTags}
        />
      </IPStack.Item>
      <IPStack.Item>
        <Checkbox
          label={t('tasks:Filter.TagGroupAnd')}
          checked={useAndForTagGroups}
          onChange={(ev, checked) => {
            onSetUseAndForTagGroups(checked ?? false);
          }}
        />
      </IPStack.Item>
      <IPStack.Item>
        <Checkbox
          checked={showTags}
          label={t('widgets:TenantTaskStatusOrgUnit.Config.LabelShowTags')}
          onChange={(ev, checked) => {
            onSetShowTags(checked ?? false);
          }}
        />
      </IPStack.Item>
    </IPStack>
  );
};
